import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ViewNav } from "@/components/ViewNav";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Inbox, Loader2, Phone, User, CheckCircle2, MessageSquareText } from "lucide-react";

export default function SmsInbox() {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | new | handled

  const load = useCallback(async () => {
    try {
      const rows = await api.getReplies();
      setReplies(rows);
    } catch {
      setReplies([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markHandled = async (id) => {
    await api.markReplyHandled(id);
    toast.success("Marked as handled");
    setReplies((prev) => prev.map((r) => (r.id === id ? { ...r, handled: true } : r)));
  };

  const shown = filter === "all" ? replies : filter === "new" ? replies.filter((r) => !r.handled) : replies.filter((r) => r.handled);
  const newCount = replies.filter((r) => !r.handled).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-[1100px] mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <Brand subtitle="377 units · 3 sites" />
          <ViewNav />
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-5 py-6 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Inbox className="h-6 w-6 text-emerald-600" /> SMS Inbox
            </h2>
            <p className="text-sm text-slate-500 mt-1">Replies customers send back to your reminder &amp; review texts. Matched to a unit by mobile number where possible. Checked every few minutes.</p>
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {[["all", "All"], ["new", `New${newCount ? ` (${newCount})` : ""}`], ["handled", "Handled"]].map(([key, label]) => (
              <button
                key={key}
                data-testid={`inbox-filter-${key}`}
                onClick={() => setFilter(key)}
                className={cn("px-3 py-1.5 rounded-md text-sm font-medium transition-all", filter === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800")}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading replies...</div>
        ) : shown.length === 0 ? (
          <div className="text-center py-20 text-slate-400 bg-white border border-slate-200 rounded-xl" data-testid="inbox-empty">
            <MessageSquareText className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            No {filter === "all" ? "" : filter + " "}replies yet.
          </div>
        ) : (
          <div className="space-y-3" data-testid="inbox-list">
            {shown.map((r) => {
              const when = r.received ? new Date(r.received).toLocaleString("en-NZ", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
              return (
                <div key={r.id} data-testid={`reply-row-${r.id}`} className={cn("rounded-xl border bg-white p-4 shadow-sm", r.handled ? "border-slate-200 opacity-80" : "border-emerald-200")}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800 flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5 text-slate-400" />
                          {r.customer_name || "Unknown sender"}
                        </span>
                        {r.unit_number && <span className="text-[11px] font-mono-nums rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5">{r.unit_number}{r.site_name ? ` · ${r.site_name}` : ""}</span>}
                        {!r.unit_id && <span className="text-[11px] rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5">Unmatched</span>}
                      </div>
                      <span className="text-xs text-slate-500 flex items-center gap-1 mt-1 font-mono-nums"><Phone className="h-3 w-3 text-slate-400" /> {r.from_phone}</span>
                    </div>
                    <span className="text-xs text-slate-400 font-mono-nums whitespace-nowrap">{when}</span>
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-line mt-3 bg-slate-50 border border-slate-100 rounded-lg p-3">{r.text}</p>
                  <div className="flex items-center justify-end mt-3">
                    {r.handled ? (
                      <span className="text-xs text-green-600 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Handled</span>
                    ) : (
                      <Button size="sm" variant="outline" data-testid={`reply-handle-${r.id}`} onClick={() => markHandled(r.id)} className="h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark handled
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
